import { throttle } from "lodash";
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "@/composables/draft-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useStekManager } from "@/composables/stek-manager";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { followLink } from "@/lib/openapi/helpers";
import { baseSuccessToastMessage } from "@/primevue/init";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";

import {
    Artefact,
    ArtefactGroupWithArtefacts,
    ArtefactMeta,
    Contributor,
    HistoryEntry,
} from ".";
import { HttpExceptionModel } from "../openapi/gen";

// Global cache for artefact downloads
const downloadCache = new Map<string, Promise<Artefact>>();

type ArtefactGroupId = string;

export async function getArtefactsTree(
    artefactGroupId: ArtefactGroupId,
    signal?: AbortSignal
): Promise<Array<ArtefactMeta | ArtefactGroupWithArtefacts>> {
    const { data, response } =
        await api.startBizArtefactManagementGetArtefactsTreeStartPost({
            body: {
                "artefact_group_id": artefactGroupId,
            },
            ...signal,
        });
    if (response.status !== 200 || !data) {
        throw new Error("Failed to fetch artefacts tree");
    }

    return data;
}

export async function get(
    artefact: ArtefactMeta,
    revision?: string | undefined
): Promise<Artefact> {
    let file_: File;
    let resp: { data: unknown; response: Response };

    // Execute request based on `artefact.links`.
    if (!artefact.links || !Object.keys(artefact.links).length) {
        const requestData: types.StartBizArtefactManagementGetArtefactStartPostData["body"] =
            {
                "artefact_id": artefact.identity,
            };

        if (revision) {
            requestData["revision"] = revision;
        }

        resp = await api.startBizArtefactManagementGetArtefactStartPost({
            body: {
                artefact_id: artefact.identity,
                revision: revision,
            },
        });
    } else {
        const query = revision
            ? new URLSearchParams({
                  revision: revision,
              })
            : undefined;
        resp = await followLink<File | Blob | string>(
            artefact,
            "get",
            undefined,
            undefined,
            query
        );
    }

    // Convert request into Artefact.
    const { data, response } = resp;

    if (!response.ok || data === undefined) {
        throw new Error(
            `File not found for: ${artefact.identity}. Response status: ${response.statusText}.`
        );
    }

    if (data instanceof File) {
        file_ = data;
    } else if (
        typeof data === "object" &&
        data !== null &&
        !(data instanceof Blob) &&
        typeof data !== "string"
    ) {
        // Hey API parses JSON as object, so we need to handle it.
        // Handle JSON data by stringifying it
        const jsonString = JSON.stringify(data, null, 2);
        file_ = new File([jsonString], artefact.name, {
            type: response.headers.get("Content-Type") || undefined,
        });
    } else {
        // Data is either a Blob or a string.
        file_ = new File([data as Blob], artefact.name, {
            type: response.headers.get("Content-Type") || undefined,
        });
    }

    return new Artefact({
        identity: artefact.identity,
        revision: revision,
        name: file_.name,
        // ContentType can includde additional information like charset.
        mimetype:
            response.headers.get("Content-Type")?.split(";")[0] ||
            file_.type ||
            "",
        content: await file_.text(),
        blob: file_,
        links: artefact.links,
    });
}

export type ContributorWithDate = Omit<Contributor, "date"> & {
    date: Date;
};
export type HistoryEntryWithDate = Omit<
    HistoryEntry,
    "author" | "committer"
> & {
    author: ContributorWithDate;
    committer: ContributorWithDate;
};

interface CreateOrUpdateArtefactOptions {
    commitMessage?: string | null;
    showToast?: boolean;
}
const defaultOptions: CreateOrUpdateArtefactOptions = {
    commitMessage: null,
    showToast: true,
};

export interface EventDetails {
    mimetype: string;
    name: string;
}

export const events = new EventTarget();

function isNotArtefactMetaGroup(
    artefact: ArtefactMeta | ArtefactGroupWithArtefacts
): artefact is ArtefactMeta {
    return !("artefacts" in artefact);
}

export const useArtefactsStore = defineStore("artefacts", () => {
    const toast = useToast();
    const { task, process, role, product, domain, stek } =
        useCurrentEntityManager();
    const isFetching = ref(false);
    const controller = ref(new AbortController());
    const syncStore = useSyncStore();

    const artefacts = ref<ArtefactMeta[]>([]);

    const entity = computed(() => {
        return (
            task.value ||
            process.value ||
            role.value ||
            product.value ||
            domain.value ||
            stek.value
        );
    });

    const fetchArtefacts = async () => {
        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!entity.value?.user_artefact_group_id) {
            return;
        }

        isFetching.value = true;
        try {
            const artefactsAndArtefactGroups = await getArtefactsTree(
                entity.value.user_artefact_group_id,
                controller.value.signal
            );
            // TODO: Introduce logic to deal with folders // nested artefact groups. - FE#373
            artefacts.value = (artefactsAndArtefactGroups || []).filter(
                isNotArtefactMetaGroup
            );
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isFetching.value = false;
        }
    };

    async function addArtefact(
        ownerId: ArtefactGroupId,
        file: File,
        opts?: CreateOrUpdateArtefactOptions
    ): Promise<ArtefactMeta> {
        const options = { ...defaultOptions, ...opts };
        const { commitMessage, showToast } = options;

        const body: types.BodyStartBizArtefactManagementAddArtefactStartPost = {
            file_: file,
            owner_id: ownerId,
        };
        if (commitMessage) {
            body.commit_message = commitMessage;
        }

        try {
            const { response, data } =
                await api.startBizArtefactManagementAddArtefactStartPost({
                    body,
                });

            if (!response.ok) {
                if (response.status === 400) {
                    toast.add({
                        severity: "error",
                        summary: "Could not save file - file is invalid",
                    });
                } else if (response.status === 413) {
                    toast.add({
                        severity: "error",
                        summary: "Could not save file - file is too large",
                    });
                } else {
                    toast.add({
                        severity: "error",
                        summary: `Could not save file - unknown error ${response.status}`,
                    });
                }
                throw response;
            }

            if (!data) {
                throw Error("No artefact created.");
            }

            // Update artefacts list
            await fetchArtefacts();

            events.dispatchEvent(
                new CustomEvent<EventDetails>("created", {
                    detail: {
                        mimetype: file.type,
                        name: file.name,
                    },
                })
            );
            if (showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: "File created",
                });
            }

            return data;
        } catch (error) {
            // Handle unexpected errors (network issues, etc.)
            const message =
                (error as HttpExceptionModel)?.detail?.[0]?.message ||
                "Unknown error";
            toast.add({
                severity: "error",
                summary: "Could not save file",
                detail: message,
            });
            // Re-throw so the calling function can handle it appropriately
            throw error;
        }
    }

    watch(
        () => entity.value?.user_artefact_group_id,
        () => {
            fetchArtefacts();
        },
        { immediate: true }
    );

    async function getHistory(
        artefact: ArtefactMeta
    ): Promise<HistoryEntryWithDate[]> {
        let history: HistoryEntry[] = [];
        if (!artefact.links || !Object.keys(artefact.links).length) {
            const { data } =
                await api.startBizArtefactManagementGetHistoryStartPost({
                    body: {
                        artefact_id: artefact.identity,
                    },
                });
            if (data && "history" in data) {
                history = data.history;
            }
        } else if (
            "links" in artefact &&
            Object.keys(artefact.links).length &&
            "history" in artefact.links
        ) {
            const { data } =
                await followLink<types.StartBizEntitiesEntityTypeEntityIdAttributeHistoryGetResponse>(
                    artefact,
                    "history"
                );
            if (data && "history" in data) {
                history = data.history as HistoryEntry[];
            }
        } else {
            throw new Error("No history available for this artefact");
        }

        return history.map((entry) => ({
            ...entry,
            author: { ...entry.author, date: new Date(entry.author.date) },
            committer: {
                ...entry.committer,
                date: new Date(entry.committer.date),
            },
        }));
    }

    async function removeArtefact(
        artefactId: string,
        commitMessage: string | null = null
    ): Promise<void> {
        const body: types.BodyStartBizArtefactManagementRemoveArtefactStartPost =
            {
                artefact_id: artefactId,
            };

        if (commitMessage) {
            body.commit_message = commitMessage;
        }

        await api.startBizArtefactManagementRemoveArtefactStartPost({ body });

        // Update artefacts list
        await fetchArtefacts();
    }

    async function restoreArtefact(
        artefact: ArtefactMeta,
        commitMessage: string | null = null
    ) {
        if (!artefact.revision) {
            throw new Error("Set a revision before restoring an artefact");
        }

        let data;

        if (
            artefact?.links &&
            Object.keys(artefact.links).length &&
            "restore" in artefact.links
        ) {
            const response =
                await followLink<types.StartBizArtefactManagementRestoreHistoricalArtefactStartPostData>(
                    artefact,
                    "restore",
                    { revision: artefact.revision }
                );
            data = response.data;
        } else {
            const body: types.BodyStartBizArtefactManagementRestoreHistoricalArtefactStartPost =
                {
                    artefact_id: artefact.identity,
                    revision: artefact.revision,
                };

            if (commitMessage) {
                body.commit_message = commitMessage;
            }

            data =
                await api.startBizArtefactManagementRestoreHistoricalArtefactStartPost(
                    { body }
                );
        }

        // Update artefacts list
        await fetchArtefacts();

        return data;
    }

    async function renameArtefact(
        artefactId: string,
        newName: string,
        commitMessage: string | null = null
    ): Promise<void> {
        const body: types.BodyStartBizArtefactManagementUpdateArtefactStartPost =
            {
                artefact_id: artefactId,
                name: newName,
            };

        if (commitMessage) {
            body.commit_message = commitMessage;
        }

        await api.startBizArtefactManagementUpdateArtefactStartPost({ body });

        // Update artefacts list
        await fetchArtefacts();
    }

    async function updateArtefactContent(
        artefact: ArtefactMeta,
        file: File,
        opts?: CreateOrUpdateArtefactOptions
    ): Promise<void> {
        const options = { ...defaultOptions, ...opts };
        const { commitMessage, showToast } = options;

        const body: types.BodyStartBizArtefactManagementUpdateArtefactStartPost =
            {
                content: file,
                artefact_id: artefact.identity,
            };
        if (commitMessage) {
            body.commit_message = commitMessage;
        }

        try {
            const { response } =
                await api.startBizArtefactManagementUpdateArtefactStartPost({
                    body,
                });
            if (!response.ok) {
                if (response.status === 400) {
                    if (artefact.mimetype === "application/bpmn+xml") {
                        toast.add({
                            severity: "error",
                            summary:
                                "Could not save file - bpmn file is invalid",
                        });
                    } else {
                        toast.add({
                            severity: "error",
                            summary: "Could not save file - file is invalid",
                        });
                    }
                } else if (response.status === 413) {
                    toast.add({
                        severity: "error",
                        summary: "Could not save file - file is too large",
                    });
                } else {
                    toast.add({
                        severity: "error",
                        summary: `Could not save file - unknown error ${response.status}`,
                    });
                }
                return; // Exit early without dispatching events or showing success toast
            }

            events.dispatchEvent(
                new CustomEvent<EventDetails>("changed", {
                    detail: {
                        mimetype: artefact.mimetype,
                        name: artefact.name,
                    },
                })
            );
            if (showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: "File updated",
                });
            }
        } catch (error) {
            // Handle unexpected errors (network issues, etc.)
            const message =
                (error as HttpExceptionModel)?.detail?.[0]?.message ||
                "Unknown error";
            toast.add({
                severity: "error",
                summary: "Could not save file",
                detail: message,
            });
            // Re-throw so the calling function can handle it appropriately
            throw error;
        }
    }

    // Listen to changes for DOCUMENT artefacts.
    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities: SyncReturn) => {
                if (
                    fullySyncedIdentities.some(
                        (identity) =>
                            identity.attribute === "name" &&
                            identity.entityType === EntityType.DOCUMENT
                    )
                ) {
                    if (stek.value) {
                        fetchArtefacts();
                    }
                }
            });
        }
    });

    return {
        artefacts,
        isFetching,
        updateArtefacts: fetchArtefacts,
        addArtefact,
        updateArtefactContent,
        removeArtefact,
        renameArtefact,
        restoreArtefact,
        getHistory,
    };
});

export const useArtefactStore = defineStore("artefact", () => {
    const { artefacts } = useArtefactsManager();

    const { artefact: artefactInfo } = useCurrentEntityManager();

    const artefact = computed<ArtefactMeta | undefined>(() => {
        const artefactId = artefactInfo.value?.identity;

        if (!artefactId) {
            return;
        }

        // No need to check if the artefact exists in the list of artefacts.
        // Better to do the fetch and use the response to determine if the
        // artefact exists.
        return artefacts.value.find(
            (artefact: ArtefactMeta) => artefact.identity === artefactId
        );
    });

    // When the selected artefact changes, make sure to forget the selected
    // revision for the previous artefact, so that we don't accidentally
    // display a wrong revision when a previously selected artefact is reselected.
    watch(artefact, (_, oldArtefact) => {
        if (oldArtefact) {
            delete oldArtefact.revision;
        }
    });

    return {
        get,
        artefacts,
        artefact,
        events,
    };
});

export const useArtefactManager = () => storeToRefs(useArtefactStore());
export const useArtefactsManager = () => storeToRefs(useArtefactsStore());

/**
 * Provides the complete artefact including content.
 *
 * The content is only fetched if the condition evaluates to true. Since the condition itself is
 * a ref, any change to it triggers the download.
 *
 * If `readonly` is provided, it will not add a draftmanager and watch for edits.
 */
export const withContent = (
    artefactMeta: Ref<ArtefactMeta | undefined>,
    condition: Ref<boolean> = ref(true),
    readonly: boolean = false
) => {
    const artefact = ref<Artefact>();
    const isFetching = ref(false);
    const syncStore = useSyncStore();

    // Track download progress
    const downloadError = ref<string>();

    // Generate cache key from artefact identity and revision
    const getCacheKey = (identity: string, revision?: string) => {
        return `${identity}::${revision || "latest"}`;
    };

    watch(
        [artefactMeta, condition],
        async () => {
            if (!artefactMeta.value || !condition.value) {
                artefact.value = undefined;
                downloadError.value = undefined;
                return;
            }

            try {
                // Set fetching state immediately
                isFetching.value = true;
                downloadError.value = undefined;

                const cacheKey = getCacheKey(
                    artefactMeta.value.identity,
                    artefactMeta.value.revision
                );

                // Check if there's an in-flight or completed download
                let downloadPromise = downloadCache.get(cacheKey);

                if (!downloadPromise) {
                    // Start new download and cache the promise
                    downloadPromise = get(
                        artefactMeta.value,
                        artefactMeta.value.revision
                    );
                    downloadCache.set(cacheKey, downloadPromise);
                }

                // Await the cached promise
                artefact.value = await downloadPromise;
            } catch (error) {
                // Remove failed download from cache to allow retry
                const cacheKey = getCacheKey(
                    artefactMeta.value.identity,
                    artefactMeta.value.revision
                );
                downloadCache.delete(cacheKey);

                // Handle download errors
                let errorMessage =
                    error instanceof Error ? error.message : String(error);

                // Special handling for socket timeout errors
                if (
                    errorMessage.includes("timeout") ||
                    errorMessage.includes("network") ||
                    errorMessage.includes("socket") ||
                    errorMessage.includes("timed out")
                ) {
                    errorMessage =
                        "Socket timeout - The file download timed out. Please check your network connection and try again.";
                }

                downloadError.value = errorMessage;
                console.error("Error fetching artefact:", error);
            } finally {
                // Always update the fetching state
                isFetching.value = false;
            }
        },
        { immediate: true, deep: true }
    );

    // When revision of the artefact changes, also update the revision of
    // the meta artefact, so that we indirectly fetch the artefact associated
    // with the new revision.
    watch(
        () => artefact.value?.revision,
        (revision) => {
            if (artefactMeta.value) {
                artefactMeta.value.revision = revision;
            }
        }
    );

    if (!readonly) {
        const queueSave = async () => {
            if (
                !artefact.value?.isModified &&
                !artefact.value?.isNameModified
            ) {
                return;
            }

            // Avoid saving when we're viewing an older version.
            if (artefactMeta.value?.revision) {
                return;
            }

            const updateContent: types.StartBizArtefactManagementUpdateArtefactStartPostData =
                {
                    body: {
                        artefact_id: artefact.value.identity,
                    },
                };

            if (artefact.value?.isNameModified) {
                updateContent.body.name = artefact.value.name;
            }

            if (artefact.value?.isModified) {
                updateContent.body.content = artefact.value.blob;
            }

            syncStore.queueSync({
                entityType: EntityType.DOCUMENT,
                args: [updateContent],
            });

            artefact.value.persist();
        };

        // Here we could just check write actions and queue them
        const entityId = computed(() => artefactMeta.value?.identity);
        const artefactDraft = useDraftManager({
            entityId,
            entityType: EntityType.DOCUMENT,
            attribute: "file_",
            artefact,
        });
        discardDraftsAfterSync(entityId, [artefactDraft], EntityType.DOCUMENT);
        const { isEditing } = useEditManager();
        const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);

        watch(
            [
                isEditing,
                () => artefact.value?.isModified,
                () => artefact.value?.isNameModified,
            ],
            ([_, modified, nameModified]) => {
                if (modified || nameModified) {
                    throttledSave();
                }
            }
        );
    }

    const { stek: stekRef } = useStekManager();
    syncChannel.addEventListener(
        "message",
        async ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.entityType === EntityType.DOCUMENT &&
                identity.entityId === artefact.value?.identity
            ) {
                if (stekRef.value) {
                    isFetching.value = true;
                    artefact.value = await get(artefact.value!);
                    isFetching.value = false;
                }
            }
        }
    );

    /**
     * Deletes the current artefact from the cache.
     */
    const deleteFromCache = () => {
        if (!artefactMeta.value) {
            return;
        }
        const cacheKey = getCacheKey(
            artefactMeta.value.identity,
            artefactMeta.value.revision
        );
        downloadCache.delete(cacheKey);
    };

    return { artefact, isFetching, downloadError, deleteFromCache };
};
